import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, ShieldCheck } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";

const VOUCHER_LABEL = {
  sales_invoice: "Sales Invoice",
  purchase_bill: "Purchase Bill",
  purchase_order: "Purchase Order",
  payment: "Payment",
  receipt: "Receipt",
  journal_voucher: "Journal Voucher",
  credit_note: "Credit Note",
  debit_note: "Debit Note",
};

export default function ApprovalRulesTab() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    voucher_type: "purchase_order", threshold_amount: 0,
    mode: "hierarchical", approver_user_ids: [], enabled: true,
  });

  const load = async () => {
    const r = await api.get("/approvals-v2/rules");
    setItems(r.data?.items || []);
    setTypes(r.data?.voucher_types || []);
    try {
      const u = await api.get("/company/users");
      setUsers(u.data || []);
    } catch { setUsers([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/approvals-v2/rules", form);
      toast.success("Approval rule added");
      setForm({ ...form, threshold_amount: 0, approver_user_ids: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this approval rule?")) return;
    try { await api.delete(`/approvals-v2/rules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="approval-rules-tab">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">// Approval matrix · admin-only</div>

      <Card className="rounded-sm p-4">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" />Add approval rule</div>
        <div className="grid md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase font-mono">Voucher type</Label>
            <Select value={form.voucher_type} onValueChange={(v) => setForm({ ...form, voucher_type: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="rule-voucher-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map(t => <SelectItem key={t} value={t}>{VOUCHER_LABEL[t] || t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Threshold ₹</Label>
            <Input type="number" value={form.threshold_amount}
                   onChange={(e) => setForm({ ...form, threshold_amount: Number(e.target.value) })}
                   className="rounded-sm mt-1 tabular-nums" data-testid="rule-threshold" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="rule-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hierarchical">Hierarchical · branch manager</SelectItem>
                <SelectItem value="user">Specific user</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Approver (if mode=user)</Label>
            <Select value={form.approver_user_ids[0] || ""} onValueChange={(v) => setForm({ ...form, approver_user_ids: v ? [v] : [] })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="rule-approver"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={save} className="rounded-sm w-full gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="rule-save">
              <Plus className="w-3.5 h-3.5" /> Add rule
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Voucher type</th><th className="text-right">Threshold ₹</th><th>Mode</th><th>Approver</th><th>Enabled</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No rules yet — every voucher will be posted without approval.</td></tr>}
            {items.map((rule) => {
              const approverName = (users.find(u => u.id === (rule.approver_user_ids || [])[0]) || {}).full_name || "—";
              return (
                <tr key={rule.id} data-testid={`rule-row-${rule.id}`}>
                  <td>{VOUCHER_LABEL[rule.voucher_type] || rule.voucher_type}</td>
                  <td className="text-right tabular-nums">{fmtINR(rule.threshold_amount)}</td>
                  <td className="capitalize">{rule.mode}</td>
                  <td className="text-xs">{rule.mode === "user" ? approverName : "Branch manager"}</td>
                  <td>{rule.enabled ? <span className="text-emerald-700 text-xs font-mono">✓ ON</span> : <span className="text-muted-foreground text-xs">off</span>}</td>
                  <td className="text-right">
                    <button onClick={() => del(rule.id)} className="text-muted-foreground hover:text-destructive" data-testid={`rule-delete-${rule.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
